import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import css from './trialLessonComplete.module.css';
import Header from '../../components/standartComponents/header';

const ArrowSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);

const SparkleSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z"/></svg>
);

const TrialLessonComplete = ({ authUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [promptVisible, setPromptVisible] = useState(false);
  const [promptHiding, setPromptHiding] = useState(false);

  const isAuthenticated = authUser?.isAuthenticated;

  useEffect(() => {
    if (!isAuthenticated) {
      const timer = setTimeout(() => setPromptVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  const dismissPrompt = () => {
    setPromptHiding(true);
    setPromptVisible(false);
    setTimeout(() => setPromptHiding(false), 450);
  };

  return (
    <>
      <Header authUser={authUser} />

      <div className={css.page}>
        <div className={css.inner}>

          {/* HERO */}
          <div className={css.hero}>
            <div className={css.sparkles}>
              {[1,2,3,4].map(i => (
                <div className={css.sp} key={i}><SparkleSvg /></div>
              ))}
            </div>
            <div className={css.sucIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h1>{t('trialComplete.title', 'Ви блискуче пройшли пробний урок!')}</h1>
            <p>{t('trialComplete.subtitle', 'Тепер оберіть, як хочете навчатися далі.')}</p>
          </div>

          {/* CARDS */}
          <div className={css.cards}>
            {/* Plan */}
            <div className={`${css.card} ${css.primary}`} onClick={() => navigate('/survey')}>
              <div className={css.badge}>★ {t('trialComplete.recommended', 'Рекомендовано')}</div>
              <div className={`${css.cardIcon} ${css.ciPlan}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              </div>
              <h3>{t('trialComplete.planTitle', 'Отримати персоналізований план')}</h3>
              <p className={css.desc}>{t('trialComplete.planDesc', 'Ми створимо курс під вашу ціль, рівень і темп навчання.')}</p>
              <div className={css.levelNote}>
                <span>💡</span>{t('trialComplete.levelNote', 'Якщо ви не знаєте свій рівень, ми запропонуємо короткий тест під час створення плану.')}
              </div>
              <button className={`${css.cardCta} ${css.ctaP}`}>
                {t('trialComplete.planCta', 'Створити мій план')}<ArrowSvg />
              </button>
            </div>

            {/* Self-study */}
            <div className={css.card} onClick={() => navigate('/allvideos')}>
              <div className={`${css.cardIcon} ${css.ciFree}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
              </div>
              <h3>{t('trialComplete.freeTitle', 'Навчатися без плану')}</h3>
              <p className={css.desc}>{t('trialComplete.freeDesc', 'Обирайте будь-які відео та практикуйтеся у власному темпі.')}</p>
              <button className={`${css.cardCta} ${css.ctaS}`} style={{ marginTop: 'auto' }}>
                {t('trialComplete.freeCta', 'Перейти до відео')}<ArrowSvg />
              </button>
            </div>
          </div>

          {/* Note */}
          <div className={css.bottomNote}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            {t('trialComplete.note', 'Ви завжди зможете перейти між цими режимами пізніше.')}
          </div>
        </div>
      </div>

      {/* FLOATING REGISTRATION PROMPT */}
      {!isAuthenticated && (
        <div className={`${css.floatPrompt} ${promptVisible ? css.visible : ''} ${promptHiding ? css.hiding : ''}`}>
          <div className={css.fpTop}>
            <div className={css.fpIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg>
            </div>
            <div className={css.fpTitle}>{t('trialComplete.promptTitle', 'Збережіть свій прогрес')}</div>
            <button className={css.fpClose} onClick={dismissPrompt}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div className={css.fpText}>{t('trialComplete.promptText', 'Створіть акаунт, щоб зберігати слова, відео та персональні плани.')}</div>
          <div className={css.fpActions}>
            <button className={css.fpRegister} onClick={() => navigate('/signup')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {t('trialComplete.register', 'Зареєструватися')}
            </button>
            <button className={css.fpLater} onClick={dismissPrompt}>{t('trialComplete.later', 'Пізніше')}</button>
          </div>
        </div>
      )}
    </>
  );
};

export default TrialLessonComplete;
